export const SAVE_AND_RESUME_EXPIRY_DAYS = 3;

const MS_PER_MINUTE = 60 * 1000;
const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/**
 * How much of the saved-progress window is left, in the coarsest unit that
 * still tells the learner something useful.
 */
export type SavedProgressRemaining =
  | { unit: "days"; days: number }
  | { unit: "hours"; hours: number }
  | { unit: "hoursMinutes"; hours: number; minutes: number }
  | { unit: "minutes"; minutes: number };

/**
 * The deadline is fixed when the attempt starts and does not move when the
 * learner saves again, so what's left has to be counted rather than quoted as
 * the full window.
 *
 * Whole days while at least one remains, then hours and minutes — a learner
 * with four hours left needs to know that, not to be told "1 day".
 *
 * The day count goes to the nearest day, not down. Because the deadline is
 * anchored at the attempt's start, flooring told a learner who exited a few
 * minutes in that they had 2 days of a 3-day window; nearest-day says 3 and is
 * closer to the truth at every other point too, over-stating by at most half a
 * day. Hours and minutes still round down, so the final day never promises
 * time the learner lacks, and the figure bottoms out at a minute rather than
 * zero while resuming is still on offer.
 *
 * Falls back to the full window when the server sent no deadline (save and
 * resume off, or an older backend), which is the copy shown before this
 * countdown existed.
 */
export function savedProgressRemaining(
  expiresAt: string | null | undefined,
  now: number = Date.now()
): SavedProgressRemaining {
  const fullWindow: SavedProgressRemaining = {
    unit: "days",
    days: SAVE_AND_RESUME_EXPIRY_DAYS,
  };
  if (!expiresAt) {
    return fullWindow;
  }

  const remainingMs = new Date(expiresAt).getTime() - now;
  if (Number.isNaN(remainingMs)) {
    return fullWindow;
  }

  const totalMinutes = Math.max(1, Math.floor(remainingMs / MS_PER_MINUTE));

  if (totalMinutes >= MINUTES_PER_DAY) {
    return { unit: "days", days: Math.round(totalMinutes / MINUTES_PER_DAY) };
  }

  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;

  if (hours === 0) {
    return { unit: "minutes", minutes };
  }
  if (minutes === 0) {
    return { unit: "hours", hours };
  }
  return { unit: "hoursMinutes", hours, minutes };
}

/**
 * Whether the saved-progress deadline has passed. Saving still succeeds after
 * it, but the next start begins a fresh attempt, so nothing may promise the
 * learner their answers are kept. False when the server sent no deadline.
 */
export function savedProgressExpired(
  expiresAt: string | null | undefined,
  now: number = Date.now()
): boolean {
  if (!expiresAt) {
    return false;
  }
  const deadline = new Date(expiresAt).getTime();
  return !Number.isNaN(deadline) && deadline <= now;
}
